import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';

const ROLE_LABEL = { student: 'Student', lecturer: 'Supervising Lecturer', alumni: 'Alumni Sponsor', judge: 'Industry Judge', admin: 'Programme Administrator' };
// Same split as the printed certificate: gold for placings and sponsors, teal otherwise.
const GOLD_CAPTIONS = ['WINNER', 'FIRST RUNNER-UP', 'SECOND RUNNER-UP', 'ALUMNI SPONSOR', 'SPONSOR'];

const GOLD = '#D4AF37';
const TEAL = '#2DD4BF';
const CREAM = '#F4ECD8';
const MUTED = '#8FA3BF';

function Row({ label, children, mono }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.25rem 1rem', padding: '0.7rem 0', borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
      <span style={{ color: MUTED, fontSize: '0.8rem' }}>{label}</span>
      <span style={{ color: mono ? GOLD : CREAM, fontSize: '0.85rem',fontFamily: mono ? 'var(--font-mono)' : 'inherit', whiteSpace: mono ? 'nowrap' : 'normal', textAlign: 'right', marginLeft: 'auto' }}>
        {children}
      </span>
    </div>
  );
}

export default function VerifyPage() {
  const { certId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/public/verify/${certId}`)
      .then(r => setResult(r.data))
      .catch(() => setResult({ valid: false }))
      .finally(() => setLoading(false));
  }, [certId]);

  const cert = result?.certificate;
  const caption = cert ? (cert.caption || (ROLE_LABEL[cert.role] || cert.role || '').toUpperCase()) : '';
  const captionColour = GOLD_CAPTIONS.includes(caption) ? GOLD : TEAL;
  const event = cert ? [cert.finale_date, cert.venue].filter(Boolean).join(' · ') : '';

  return (
    <div style={{ minHeight: '100vh', background: '#0D1023', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem', boxSizing: 'border-box', fontFamily: 'var(--font-body)' }}>
      <Link to="/" style={{ marginBottom: '1.5rem', display: 'block' }}>
        <img src="/futurum-logo.png" alt="Futurum Innovation Awards" style={{ height: 72 }} onError={e => { e.target.style.display = 'none'; }} />
      </Link>

      <div style={{ boxSizing: 'border-box', width: '100%', maxWidth: 560, background: '#121733', border: `1.5px solid ${result?.valid ? '#C9A84C' : '#ef4444'}`, borderRadius: 16, padding: 'clamp(1.25rem, 5vw, 2.5rem)', textAlign: 'center' }}>
        {loading ? (
          <p style={{ color: MUTED, margin: 0 }}>Verifying certificate…</p>
        ) : !result?.valid ? (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }} aria-hidden="true">❌</div>
            <h1 style={{ color: '#ef4444', fontFamily: 'var(--font-display)', fontSize: 'clamp(1.3rem, 5.5vw, 1.75rem)', lineHeight: 1.2, margin: '0 0 0.75rem' }}>Certificate not found</h1>
            <p style={{ color: MUTED, fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
              The certificate ID <code style={{ color: GOLD, overflowWrap: 'anywhere' }}>{certId}</code> could not be verified. It may be mistyped or not yet issued.
            </p>
          </>
        ) : (
          <>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(45,212,191,0.12)', color: TEAL, border: `1px solid ${TEAL}`, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.12em', padding: '4px 12px', borderRadius: 999, marginBottom: '1.25rem' }}>
              ✓ VERIFIED CERTIFICATE
            </div>
            <div style={{ color: MUTED, fontSize: '0.75rem', letterSpacing: '0.18em', marginBottom: '0.4rem' }}>PRESENTED TO</div>
            <h1 style={{ color: GOLD, fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 500, fontSize: 'clamp(1.5rem, 6.5vw, 2.1rem)', lineHeight: 1.2, margin: '0 0 0.75rem' }}>
              {cert.display_name}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: '1.25rem' }}>
              <span style={{ width: 32, height: 1, background: '#C9A84C' }} />
              <span style={{ color: captionColour, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.16em' }}>{caption}</span>
              <span style={{ width: 32, height: 1, background: '#C9A84C' }} />
            </div>
            {cert.project_title && (
              <p style={{ color: CREAM, fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: '1rem', lineHeight: 1.5, margin: '0 0 1.25rem' }}>
                {cert.project_title}
              </p>
            )}
            <div style={{ textAlign: 'left', borderTop: '1px solid rgba(201,168,76,0.15)' }}>
              <Row label="Awarded by">Futurum Innovation Awards {cert.season}</Row>
              {cert.placing && <Row label="Placing">{cert.placing}</Row>}
              {event && <Row label="Grand Finale">{event}</Row>}
              {cert.institution_name && <Row label="Institution">{cert.institution_name}</Row>}
              <Row label="Issued">{new Date(cert.issued_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</Row>
              <Row label="Certificate ID" mono>{cert.cert_id}</Row>
            </div>
          </>
        )}
      </div>

      <p style={{ color: MUTED, fontSize: '0.75rem', marginTop: '1.5rem', textAlign: 'center' }}>
        Futurum Innovation Awards · futurumawards.com
      </p>
    </div>
  );
}
